import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

interface NpsModalState {
  showModal: boolean;
  requestId: string | null;
  loading: boolean;
  npsQuestion: string | null;
}

const DISMISSAL_KEY = 'nps_modal_dismissed_until';
const DELAY_MS = 5000; // 10 segundos
const NPS_ENABLED = process.env.REACT_APP_NPS_ENABLED === 'true';

export const useNpsModal = () => {
  const { user, isAuthenticated } = useAuth0();
  const [state, setState] = useState<NpsModalState>({
    showModal: false,
    requestId: null,
    loading: false,
    npsQuestion: null,
  });

  useEffect(() => {
    if (!NPS_ENABLED || !isAuthenticated || !user) return;

    // Verificar se modal foi dispensado recentemente
    const dismissedUntil = localStorage.getItem(DISMISSAL_KEY);
    if (dismissedUntil) {
      const dismissalDate = new Date(dismissedUntil);
      if (dismissalDate > new Date()) {
        return; // Ainda dentro do período de 7 dias
      } else {
        localStorage.removeItem(DISMISSAL_KEY); // Expirou
      }
    }

    // Timer de 10 segundos
    const timer = setTimeout(async () => {
      await checkAndShowModal();
    }, DELAY_MS);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  const checkAndShowModal = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      // Sempre usar o customer "konfidency" para NPS do dashboard
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/konfidency/nps/request-by-user`
      );
      const { requestId, hasResponded, surveyActive, npsQuestion } = response.data;

      // Só exibe modal se:
      // 1. Survey está ativa
      // 2. Usuário não respondeu
      // 3. Tem requestId (foi criado ou já existia)
      if (surveyActive && !hasResponded && requestId) {
        setState({
          showModal: true,
          requestId: requestId,
          loading: false,
          npsQuestion: npsQuestion || null,
        });
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Erro ao verificar NPS request:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleScoreClick = (score: number) => {
    if (!state.requestId) return;

    // Redirecionar para kfy.app seguindo o padrão: /nps/:customer/:requestId?npsRating=X
    const url = `https://www.kfy.app/nps/konfidency/${state.requestId}?npsRating=${score}`;
    window.open(url, '_blank');

    // Fechar modal
    setState(prev => ({ ...prev, showModal: false }));
  };

  const handleDismiss = () => {
    // Salvar data de dismissal (7 dias)
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + 7);
    localStorage.setItem(DISMISSAL_KEY, dismissUntil.toISOString());

    // Fechar modal
    setState(prev => ({ ...prev, showModal: false }));
  };

  return {
    showModal: state.showModal,
    requestId: state.requestId,
    loading: state.loading,
    npsQuestion: state.npsQuestion,
    handleScoreClick,
    handleDismiss,
  };
};
